import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Button, Grid, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, IconButton, useTheme, useMediaQuery,
} from "@mui/material";
import {
  Groups as GroupsIcon, CallSplit as SplitIcon,
  Phone as PhoneIcon, Timer as TimerIcon, CheckCircle as CheckIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import ioClient from "socket.io-client";
import { bindGameResync } from "../../../utils/bindGameResync";
import useGameSounds from "../../../hooks/useGameSounds";
import SoundMuteButton from "../../Common/SoundMuteButton";
import { getGuestDisplayName } from "../../../utils/guestDisplayUtils";
import FFFLeaderboardView from "./FFFLeaderboardView";
import FFFIntroView from "./FFFIntroView";
import BeerillionerWaitingView from "./BeerillionerWaitingView";
import BeerillionerGetReadyView from "./BeerillionerGetReadyView";
import HotSeatAnnounceModal from "../../games/HotSeatAnnounceModal";
import { playSound } from "../../../utils/gameAudio";
import { HOL_HOT_SEAT_CLAP_URL } from "../../../constants/holDefaultSounds";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getDeviceId = () => {
  let id = localStorage.getItem("beer_device_id");
  if (!id) { id = `beer_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`; localStorage.setItem("beer_device_id", id); }
  return id;
};

const getDisplayName = () => {
  try {
    const authUser = localStorage.getItem("user");
    if (authUser) return getGuestDisplayName(JSON.parse(authUser));
    const guestUser = localStorage.getItem("guest_user");
    if (guestUser) return getGuestDisplayName(JSON.parse(guestUser));
    const legacy = localStorage.getItem("user_data");
    if (legacy) return getGuestDisplayName(JSON.parse(legacy));
  } catch { /* ignore */ }
  return localStorage.getItem("beer_display_name") || "Guest";
};

const BeerillionerScreen = ({ spectatorMode = false }) => {
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const deviceId = useMemo(getDeviceId, []);
  const displayName = useMemo(getDisplayName, []);
  const [fffQuestion, setFffQuestion] = useState(null);
  const [fffTimer, setFffTimer] = useState(0);
  const [fffAnswer, setFffAnswer] = useState(null);
  const [fffSubmitted, setFffSubmitted] = useState(false);
  const [fffResult, setFffResult] = useState(null);
  const [fffTimeTaken, setFffTimeTaken] = useState(null);
  const [fffCorrectAnswer, setFffCorrectAnswer] = useState(null);
  const [fffRound, setFffRound] = useState(1);
  const [fffCountdown, setFffCountdown] = useState(0);
  const [fffTimerStarted, setFffTimerStarted] = useState(false);
  const [fffLeaderboardVisible, setFffLeaderboardVisible] = useState(false);
  const [fffPublicLeaderboard, setFffPublicLeaderboard] = useState([]);
  const [fffReveal, setFffReveal] = useState(null);
  const timerRef = useRef(null);
  const fffDeadlineMsRef = useRef(null);
  const [gameQuestion, setGameQuestion] = useState(null);
  const [gameOptions, setGameOptions] = useState(null);
  const [, setGamePrize] = useState(null);
  const [highlightedAnswer, setHighlightedAnswer] = useState(null);
  const [revealResult, setRevealResult] = useState(null);
  const [removedOptions, setRemovedOptions] = useState([]);
  const [hotSeatPlayer, setHotSeatPlayer] = useState(null);
  const [hotSeatAnnounce, setHotSeatAnnounce] = useState(null);
  const [lifelinesRemaining, setLifelinesRemaining] = useState([]);
  const [audienceResults, setAudienceResults] = useState(null);
  const [audienceGraphVisible, setAudienceGraphVisible] = useState(false);
  const [audienceLifelinePending, setAudienceLifelinePending] = useState(false);
  const [gameOver, setGameOver] = useState(null);
  const [revealedOptions, setRevealedOptions] = useState({});
  const [shownOptions, setShownOptions] = useState([]);
  const [questionTimerValue, setQuestionTimerValue] = useState(null);
  const questionTimerRef = useRef(null);
  const [ladderModalOpen, setLadderModalOpen] = useState(false);
  const [showPrizeLadder, setShowPrizeLadder] = useState(false);
  const prizeLadderTimerRef = useRef(null);
  const gameOverTimerRef = useRef(null);
  const [audiencePlayActive, setAudiencePlayActive] = useState(false);
  const [audiencePlayTimer, setAudiencePlayTimer] = useState(0);
  const [audiencePlayPaused, setAudiencePlayPaused] = useState(false);
  const [audiencePlayLocked, setAudiencePlayLocked] = useState(false);
  const [audiencePlayAnswer, setAudiencePlayAnswer] = useState(null);
  const [, setAudiencePlayStats] = useState(null);
  const audiencePlayTimerRef = useRef(null);
  const [clueVisible, setClueVisible] = useState(false);
  const [songNameVisible, setSongNameVisible] = useState(false);
  const [questionClue, setQuestionClue] = useState(null);
  const [songName, setSongName] = useState(null);
  const socketRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { play, playLoop, tryStartBgMusic, stopBgMusic, loaded: soundsLoaded, sounds } = useGameSounds("beerillioner");
  const playRef = useRef(play);
  const playLoopRef = useRef(playLoop);
  const soundsRef = useRef(sounds);
  const sessionStatusRef = useRef(null);
  const fffTimerEndPlayedRef = useRef(false);
  const fffLeaderboardVisibleRef = useRef(false);
  /** Skip idle BG restart when session_created already started it (avoids clash). */
  const skipIdleBgOnceRef = useRef(false);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  useEffect(() => {
    playLoopRef.current = playLoop;
  }, [playLoop]);

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    sessionStatusRef.current = session?.status || null;
  }, [session?.status]);

  useEffect(() => {
    fffLeaderboardVisibleRef.current = fffLeaderboardVisible;
  }, [fffLeaderboardVisible]);

  // 1. Idle lobby BG only when no active session (or ended). Idle uses Get-ready BG (bg_music_2).
  useEffect(() => {
    if (!soundsLoaded) return;
    if (!session || session.status === "ended") {
      tryStartBgMusic();
    } else if (session.status === "idle") {
      if (skipIdleBgOnceRef.current) {
        skipIdleBgOnceRef.current = false;
        return;
      }
      playLoop("bg_music_2");
    } else if (session.status === "fff_intro") {
      if (sounds.fff_intro) playLoop("fff_intro");
      else playLoop("bg_music_2");
    }
  }, [session?.id, session?.status, soundsLoaded, tryStartBgMusic, playLoop, sounds.fff_intro]);

  useEffect(() => {
    if (fffResult === "correct") playRef.current("correct");
    else if (fffResult === "wrong") playRef.current("wrong");
  }, [fffResult]);

  useEffect(() => {
    if (!revealResult) return;
    if (revealResult.is_correct) playRef.current("correct");
    else playRef.current("wrong");
  }, [revealResult]);

  useEffect(() => {
    if (!gameOver) return;
    if (gameOver.reason === "won") playRef.current("win");
    else if (gameOver.reason === "timer") playRef.current("wrong");
  }, [gameOver]);

  // 6. FFF timer end sound (once per round, when deadline hits)
  useEffect(() => {
    if (session?.status !== "fff" || !fffTimerStarted) return;
    const deadline = fffDeadlineMsRef.current;
    if (!deadline) return;
    // Already expired when attaching (e.g. page refresh) — don't replay
    if (Date.now() >= deadline) {
      fffTimerEndPlayedRef.current = true;
      return;
    }
    const check = () => {
      if (Date.now() >= deadline && !fffTimerEndPlayedRef.current) {
        fffTimerEndPlayedRef.current = true;
        playRef.current("fff_timer_end");
        stopBgMusic();
      }
    };
    const id = setInterval(check, 250);
    return () => clearInterval(id);
  }, [fffTimerStarted, fffTimer > 0, session?.status, stopBgMusic]);

  useEffect(() => {
    if (!soundsLoaded) return;
    if (fffLeaderboardVisible && session?.status === "fff") {
      playLoop("fff_leaderboard");
    }
  }, [fffLeaderboardVisible, session?.status, soundsLoaded, playLoop]);

  const loadState = useCallback(async () => {
    try {
      const q = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
      const res = await fetch(`${API_BASE}/api/beerillioner/sessions/active${q}`);
      const data = await res.json();
      if (data.success) {
        setSession(data.session); setConfig(data.config);
        if (!data.session) {
          fffDeadlineMsRef.current = null;
          setFffQuestion(null); setFffTimer(0); setFffCountdown(0);
          setFffSubmitted(false); setFffAnswer(null); setFffResult(null);
          setFffTimeTaken(null); setFffTimerStarted(false); setFffCorrectAnswer(null); setFffRound(1);
          setFffLeaderboardVisible(false); setFffPublicLeaderboard([]); setFffReveal(null);
          setGameQuestion(null); setGameOptions(null); setRemovedOptions([]); setRevealedOptions({}); setShownOptions([]);
          setQuestionTimerValue(null); setAudiencePlayActive(false); setAudiencePlayLocked(false);
          setAudiencePlayPaused(false); setAudiencePlayAnswer(null); setAudiencePlayStats(null); setAudiencePlayTimer(0);
          setAudienceResults(null); setAudienceGraphVisible(false); setAudienceLifelinePending(false);
          setClueVisible(false); setSongNameVisible(false); setQuestionClue(null); setSongName(null);
          setShowPrizeLadder(false);
        } else if (data.session) {
          if (data.session.status !== "ended") setGameOver(null);
          setLifelinesRemaining(JSON.parse(data.session.lifelines_remaining || "[]"));
          setHotSeatPlayer(data.session.hot_seat_display_name || null);
          setHighlightedAnswer(data.session.highlighted_answer || null);
          if (data.session.status !== "main_game") {
            setGameQuestion(null); setGameOptions(null); setRemovedOptions([]); setRevealedOptions({}); setShownOptions([]);
            setQuestionTimerValue(null); setAudiencePlayActive(false); setAudiencePlayLocked(false);
            setAudiencePlayPaused(false); setAudiencePlayAnswer(null); setAudiencePlayStats(null); setAudiencePlayTimer(0);
            setClueVisible(false); setSongNameVisible(false); setQuestionClue(null); setSongName(null);
            setShowPrizeLadder(false);
          }
          setAudienceLifelinePending(!!data.session.audience_lifeline_pending);
          if (data.session.audience_graph_visible) {
            try {
              const votes = JSON.parse(data.session.audience_votes || "{}");
              if (votes.percentages) { setAudienceResults({ percentages: votes.percentages, total_votes: votes.total_votes }); setAudienceGraphVisible(true); }
            } catch {}
          }
          if (data.session.status === "fff" && data.activeFFF?.question) {
            setFffQuestion(data.activeFFF.question);
            setFffRound(data.activeFFF.round_number || 1);
            setFffCountdown(0);
            setFffCorrectAnswer(data.activeFFF.correct_answer || null);
            setShownOptions(Array.isArray(data.activeFFF.shown_options) ? data.activeFFF.shown_options : []);
            setFffTimerStarted(!!data.activeFFF.started_at_ms);
            const endedAtMs = data.activeFFF.ended_at_ms || null;
            if (endedAtMs) {
              fffDeadlineMsRef.current = null;
              setFffTimer(0);
            } else if (data.activeFFF.started_at_ms && data.activeFFF.timer_seconds) {
              const deadline = data.activeFFF.started_at_ms + data.activeFFF.timer_seconds * 1000;
              fffDeadlineMsRef.current = deadline;
              const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
              setFffTimer(remaining);
            } else {
              fffDeadlineMsRef.current = null;
              setFffTimer(0);
            }
            // Reloaded inside the reveal window — keep showing the answer until
            // the server flips the leaderboard on.
            const revealMs = (data.activeFFF.reveal_seconds || 5) * 1000;
            if (endedAtMs && !data.session.fff_leaderboard_visible && Date.now() - endedAtMs < revealMs) {
              setFffReveal({
                correct_answer: data.activeFFF.correct_answer || null,
                options: data.activeFFF.question || null,
                round_number: data.activeFFF.round_number ?? null,
              });
            } else {
              setFffReveal(null);
            }
            if (data.fffMyAnswer) {
              setFffSubmitted(true);
              setFffAnswer(data.fffMyAnswer.answer);
              setFffResult(data.fffMyAnswer.is_correct ? "correct" : "wrong");
              if (data.fffMyAnswer.time_taken_seconds != null) setFffTimeTaken(data.fffMyAnswer.time_taken_seconds);
            } else {
              setFffSubmitted(false);
              setFffAnswer(null);
              setFffResult(null);
              setFffTimeTaken(null);
            }
          } else {
            fffDeadlineMsRef.current = null;
            setFffQuestion(null);
            setFffTimer(0);
            setFffCountdown(0);
            setFffSubmitted(false);
            setFffAnswer(null);
            setFffResult(null);
            setFffTimeTaken(null);
            setFffTimerStarted(false);
            setFffCorrectAnswer(null);
            setFffRound(1);
            setFffLeaderboardVisible(false);
            setFffPublicLeaderboard([]);
            setFffReveal(null);
          }
          if (data.session.status === "fff" && data.session.fff_leaderboard_visible && data.fffLeaderboard) {
            setFffLeaderboardVisible(true);
            setFffPublicLeaderboard(data.fffLeaderboard.all || []);
          } else if (data.session.status !== "fff" || !data.session.fff_leaderboard_visible) {
            setFffLeaderboardVisible(false);
            if (data.session.status !== "fff") setFffPublicLeaderboard([]);
          }
          if (data.session.status === "main_game" && data.session.current_question_data) {
            try {
              const q = JSON.parse(data.session.current_question_data);
              setGameQuestion({
                id: data.session.current_question_id || data.session.current_quiz_question_id || null,
                question_text: q.question_text,
                level: data.session.current_level || 0,
              });
              if (q.question_clue) setQuestionClue(q.question_clue);
              if (q.song_name) setSongName(q.song_name);
              setClueVisible(!!data.session.clue_visible);
              setSongNameVisible(!!data.session.song_name_visible);
              let sessionShown = [];
              try { sessionShown = JSON.parse(data.session.shown_options || "[]"); } catch { sessionShown = []; }
              let sessionRemoved = [];
              try { sessionRemoved = JSON.parse(data.session.removed_options || "[]"); } catch { sessionRemoved = []; }
              if (data.session.options_revealed || sessionShown.length > 0) {
                const opts = {};
                const effectiveShown = data.session.options_revealed ? ["A","B","C","D"] : sessionShown;
                effectiveShown.forEach(opt => {
                  opts[`option_${opt.toLowerCase()}`] = q[`option_${opt.toLowerCase()}`];
                });
                setGameOptions(prev => ({ ...(prev || {}), ...opts }));
                setShownOptions(effectiveShown);
              }
              if (sessionRemoved.length > 0) setRemovedOptions(sessionRemoved);
            } catch { /* ignore invalid JSON */ }
          }
          if (!spectatorMode && data.session.status === "main_game" && data.config?.audience_play_enabled) {
            if (data.session.audience_play_active && data.session.audience_timer_end_ms) {
              const remaining = Math.max(0, Math.ceil((data.session.audience_timer_end_ms - Date.now()) / 1000));
              if (remaining > 0) {
                setAudiencePlayActive(true); setAudiencePlayTimer(remaining);
                if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
                audiencePlayTimerRef.current = setInterval(() => { setAudiencePlayTimer(prev => { if (prev <= 1) { clearInterval(audiencePlayTimerRef.current); return 0; } return prev - 1; }); }, 1000);
              }
              else { setAudiencePlayLocked(true); }
            } else if (data.session.audience_answers_locked) { setAudiencePlayLocked(true); }
            else if (data.session.audience_timer_paused_remaining_ms > 0) { setAudiencePlayPaused(true); setAudiencePlayTimer(Math.ceil(data.session.audience_timer_paused_remaining_ms / 1000)); }
            if (data.session.audience_play_active || data.session.audience_answers_locked || data.session.audience_timer_paused_remaining_ms > 0) {
              try {
                const apRes = await fetch(`${API_BASE}/api/beerillioner/sessions/${data.session.id}/audience-play/state?device_id=${deviceId}`);
                const apData = await apRes.json();
                if (apData.success && apData.my_answer) setAudiencePlayAnswer(apData.my_answer);
                if (apData.success && apData.stats) setAudiencePlayStats(apData.stats);
              } catch { /* ignore */ }
            }
          }
        }
      }
    } catch (err) { console.error("Failed to load state:", err); }
    finally { setLoading(false); }
  }, [deviceId, spectatorMode]);

  useEffect(() => { loadState(); }, [loadState]);

  useEffect(() => {
    const socket = ioClient(API_BASE); socketRef.current = socket;
    const joinRoom = () => {
      socket.emit("beer:join-global", { device_id: deviceId, display_name: displayName, spectator: spectatorMode });
    };
    const onConnect = () => {
      joinRoom();
      loadState();
    };
    socket.on("connect", onConnect);
    const unbindResync = bindGameResync(() => {
      if (socket.connected) joinRoom();
      loadState();
    });
    socket.on("beer:session_created", (d) => {
      setSession(d.session);
      sessionStatusRef.current = d.session?.status || "idle";
      setGameOver(null);
      setFffSubmitted(false);
      setFffAnswer(null);
      setFffResult(null);
      setRevealResult(null);
      setGameQuestion(null);
      setGameOptions(null);
      setRemovedOptions([]);
      setRevealedOptions({});
      setShownOptions([]);
      setAudienceResults(null);
      setAudienceGraphVisible(false);
      setAudienceLifelinePending(false);
      setFffLeaderboardVisible(false);
      setFffPublicLeaderboard([]);
      setFffReveal(null);
      setHotSeatAnnounce(null);
      // 2. Start new game — stop prior lobby/intro BG, then clap once + Get ready BG loop
      skipIdleBgOnceRef.current = true;
      stopBgMusic();
      playRef.current("session_start_clap");
      playLoopRef.current("bg_music_2");
    });
    socket.on("beer:fff_intro", () => {
      fffDeadlineMsRef.current = null;
      sessionStatusRef.current = "fff_intro";
      setSession((p) => (p ? { ...p, status: "fff_intro" } : p));
      setFffQuestion(null);
      setFffTimer(0);
      setFffCountdown(0);
      setFffTimerStarted(false);
      setFffSubmitted(false);
      setFffAnswer(null);
      setFffResult(null);
      setFffTimeTaken(null);
      setFffLeaderboardVisible(false);
      setFffPublicLeaderboard([]);
      setHotSeatAnnounce(null);
      if (soundsRef.current?.fff_intro?.audio_url) {
        playLoopRef.current("fff_intro");
      } else {
        playLoopRef.current("bg_music_2");
      }
    });
    socket.on("beer:fff_round_ended", (d) => {
      fffDeadlineMsRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      setFffTimer(0);
      setFffTimerStarted(true);
      if (d?.correct_answer) setFffCorrectAnswer(d.correct_answer);
      setFffReveal({
        correct_answer: d?.correct_answer || null,
        options: d?.options || null,
        round_number: d?.round_number ?? null,
      });
      if (!fffTimerEndPlayedRef.current) {
        fffTimerEndPlayedRef.current = true;
        playRef.current("fff_timer_end");
        stopBgMusic();
      }
    });
    socket.on("beer:fff_started", (d) => {
      fffDeadlineMsRef.current = null;
      fffTimerEndPlayedRef.current = false;
      sessionStatusRef.current = "fff";
      setFffReveal(null);
      setSession(p => p ? { ...p, status: "fff" } : p);
      setFffQuestion(d.question);
      setFffCorrectAnswer(d.correct_answer);
      setFffRound(d.round_number);
      setFffCountdown(3);
      setFffTimer(0);
      setFffTimerStarted(false);
      setFffSubmitted(false);
      setFffAnswer(null);
      setFffResult(null);
      setFffTimeTaken(null);
      setShownOptions([]);
      setFffLeaderboardVisible(false);
      setFffPublicLeaderboard([]);
      setHotSeatAnnounce(null);
      setGameOver(null);
      // 3. Push FFF — question reading BG loop
      playLoopRef.current("fff_question_reading");
    });
    socket.on("beer:fff_leaderboard_toggled", (d) => {
      const visible = !!d?.visible;
      const wasVisible = fffLeaderboardVisibleRef.current;
      fffLeaderboardVisibleRef.current = visible;
      if (visible) setFffReveal(null);
      setFffLeaderboardVisible(visible);
      setFffPublicLeaderboard(visible ? (d.all || []) : []);
      if (visible && !wasVisible) {
        playLoopRef.current("fff_leaderboard");
      } else if (!visible && sessionStatusRef.current === "fff") {
        stopBgMusic();
      }
    });
    socket.on("beer:hot_seat_selected", (d) => {
      fffDeadlineMsRef.current = null;
      sessionStatusRef.current = "main_game";
      setFffReveal(null);
      setHotSeatPlayer(d.display_name);
      setSession(p => p ? { ...p, status: "main_game", hot_seat_display_name: d.display_name, hot_seat_player_id: d.player_id ?? p.hot_seat_player_id } : p);
      setFffCountdown(0);
      setGameQuestion(null);
      setGameOptions(null);
      setRevealResult(null);
      setRemovedOptions([]);
      setRevealedOptions({});
      setShownOptions([]);
      setShowPrizeLadder(false);
      setHotSeatAnnounce({
        display_name: d.display_name,
        profile_picture: d.profile_picture || null,
        player_id: d.player_id || "",
      });
      if (soundsRef.current?.fff_hot_seat?.audio_url) {
        playRef.current("fff_hot_seat");
      } else {
        playSound(HOL_HOT_SEAT_CLAP_URL, { volume: 1, loop: false });
      }
      playRef.current("mg_intro");
      stopBgMusic();
    });
    socket.on("beer:question_pushed", (d) => {
      setHotSeatAnnounce(null);
      setFffLeaderboardVisible(false);
      setFffPublicLeaderboard([]);
      setFffReveal(null);
      setFffQuestion(null);
      setGameQuestion(d.question); setGameOptions(null); setGamePrize(d.prize);
      setHighlightedAnswer(null); setRevealResult(null); setRemovedOptions([]); setRevealedOptions({}); setShownOptions([]);
      setQuestionTimerValue(null); if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setAudienceResults(null); setAudienceGraphVisible(false); setAudienceLifelinePending(false);
      setAudiencePlayActive(false); setAudiencePlayLocked(false); setAudiencePlayPaused(false);
      setAudiencePlayAnswer(null); setAudiencePlayStats(null); setAudiencePlayTimer(0);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      setClueVisible(false); setSongNameVisible(false); setQuestionClue(null); setSongName(null);
      setShowPrizeLadder(false);
      if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
      if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
      setSession(p => p ? { ...p, current_level: d.question?.level, current_question_id: d.question?.id, options_revealed: 0 } : p);
      playLoopRef.current("mg_bg");
    });
    socket.on("beer:options_revealed", (d) => { setGameOptions(d.options); setShownOptions(["A","B","C","D"]); });
    socket.on("beer:all_options_shown", (d) => {
      if (d.options) setGameOptions((prev) => ({ ...(prev || {}), ...d.options }));
      if (Array.isArray(d.shown)) setShownOptions(d.shown);
      // 4. Show All options (FFF)
      if (sessionStatusRef.current === "fff") playRef.current("show_all_options");
    });
    socket.on("beer:option_shown", (d) => {
      setGameOptions((prev) => ({ ...(prev || {}), [`option_${d.option.toLowerCase()}`]: d.option_text }));
      setShownOptions((prev) => (prev.includes(d.option) ? prev : [...prev, d.option]));
      // 4. Show single option (FFF)
      if (sessionStatusRef.current === "fff") playRef.current("show_option");
    });
    socket.on("beer:option_hidden", (d) => {
      setShownOptions((prev) => prev.filter((x) => x !== d.option));
    });
    socket.on("beer:next_question", () => {
      fffDeadlineMsRef.current = null;
      setGameQuestion(null);
      setGameOptions(null);
      setHighlightedAnswer(null);
      setRevealResult(null);
      setRemovedOptions([]);
      setRevealedOptions({});
      setShownOptions([]);
      setQuestionTimerValue(null);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setAudienceResults(null);
      setAudienceGraphVisible(false);
      setAudienceLifelinePending(false);
      setAudiencePlayActive(false);
      setAudiencePlayLocked(false);
      setAudiencePlayPaused(false);
      setAudiencePlayAnswer(null);
      setAudiencePlayStats(null);
      setAudiencePlayTimer(0);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      setClueVisible(false); setSongNameVisible(false); setQuestionClue(null); setSongName(null);
      setShowPrizeLadder(false);
      if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
    });
    socket.on("beer:answer_highlighted", (d) => {
      setHighlightedAnswer(d.answer);
      if (sessionStatusRef.current === "main_game") {
        playRef.current("mg_lock");
        stopBgMusic();
      }
    });
    socket.on("beer:answer_revealed", (d) => {
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setQuestionTimerValue(null);
      setRevealResult(d);
      stopBgMusic();
      if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
      if (d.is_correct) {
        prizeLadderTimerRef.current = setTimeout(() => setShowPrizeLadder(true), 2500);
      }
    });
    socket.on("beer:option_revealed", (d) => { setRevealedOptions(prev => ({ ...prev, [d.option]: d.is_correct })); });
    socket.on("beer:timer_started", (d) => {
      if (d.context === "fff") {
        if (d.deadline_ms) fffDeadlineMsRef.current = d.deadline_ms;
        else fffDeadlineMsRef.current = null;
        const rem = d.deadline_ms
          ? Math.max(0, Math.ceil((d.deadline_ms - Date.now()) / 1000))
          : d.seconds;
        setFffTimer(rem);
        setFffTimerStarted(true);
        fffTimerEndPlayedRef.current = false;
        // 5. FFF timer BG loop
        playLoopRef.current("fff_timer_bg");
      } else {
        setQuestionTimerValue(d.seconds); if (questionTimerRef.current) clearInterval(questionTimerRef.current);
        questionTimerRef.current = setInterval(() => { setQuestionTimerValue(p => { if (p <= 1) { clearInterval(questionTimerRef.current); return 0; } return p - 1; }); }, 1000);
      }
    });
    socket.on("beer:lifeline_used", (d) => {
      setLifelinesRemaining(d.remaining_lifelines || []);
      if (d.type === "50_50" && d.result?.removed) setRemovedOptions(d.result.removed);
      if (d.type === "ask_audience") {
        setAudienceGraphVisible(false);
        if (d.result?.pending) { setAudienceLifelinePending(true); setAudienceResults(null); }
        else if (d.result?.percentages) { setAudienceResults({ percentages: d.result.percentages, total_votes: d.result.total_votes }); setAudienceLifelinePending(false); }
      }
      const ext = parseInt(d.timer_extension_seconds, 10) || 0;
      if (ext > 0) setQuestionTimerValue((p) => (p != null ? p + ext : p));
    });
    socket.on("beer:audience_lifeline_ready", (d) => {
      setAudienceResults({ percentages: d.percentages, total_votes: d.total_votes });
      setAudienceLifelinePending(false);
    });
    socket.on("beer:audience_graph_toggled", (d) => {
      setAudienceGraphVisible(d.visible);
      if (d.visible && d.percentages) setAudienceResults({ percentages: d.percentages, total_votes: d.total_votes });
    });
    socket.on("beer:game_over", (d) => {
      fffDeadlineMsRef.current = null;
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
      setQuestionTimerValue(null);
      stopBgMusic();
      setAudiencePlayActive(false); setAudiencePlayLocked(false); setAudiencePlayPaused(false);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      if (d.reason === "admin_ended" && d.reset) {
        if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
        if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
        setShowPrizeLadder(false);
        setSession(null); setGameOver(null); setGameQuestion(null); setGameOptions(null); setRevealResult(null); setRemovedOptions([]);
        return;
      }
      const applyOver = () => {
        setGameOver(d);
        setSession(p => p ? { ...p, status: "ended" } : p);
        setShowPrizeLadder(false);
      };
      if (d.reason === "wrong" || d.reason === "won") {
        if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
        setShowPrizeLadder(false);
        if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
        gameOverTimerRef.current = setTimeout(applyOver, 2500);
      } else {
        applyOver();
      }
    });
    socket.on("beer:clue_toggled", (d) => {
      setClueVisible(d.visible);
      if (d.visible && d.clue) setQuestionClue(d.clue);
    });
    socket.on("beer:song_name_toggled", (d) => {
      setSongNameVisible(d.visible);
      if (d.visible && d.song_name) setSongName(d.song_name);
    });
    socket.on("beer:audience_play_started", (d) => {
      setAudiencePlayActive(true); setAudiencePlayLocked(false); setAudiencePlayPaused(false);
      setAudiencePlayAnswer(null); setAudiencePlayStats(null);
      setAudiencePlayTimer(d.timer_seconds);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      audiencePlayTimerRef.current = setInterval(() => { setAudiencePlayTimer(prev => { if (prev <= 1) { clearInterval(audiencePlayTimerRef.current); return 0; } return prev - 1; }); }, 1000);
    });
    socket.on("beer:audience_play_paused", () => {
      setAudiencePlayPaused(true); setAudiencePlayActive(false);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
    });
    socket.on("beer:audience_play_resumed", (d) => {
      setAudiencePlayPaused(false); setAudiencePlayActive(true);
      setAudiencePlayTimer(d.remaining_seconds);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      audiencePlayTimerRef.current = setInterval(() => { setAudiencePlayTimer(prev => { if (prev <= 1) { clearInterval(audiencePlayTimerRef.current); return 0; } return prev - 1; }); }, 1000);
    });
    socket.on("beer:audience_answers_locked", (d) => {
      setAudiencePlayLocked(true); setAudiencePlayActive(false); setAudiencePlayPaused(false);
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      setAudiencePlayStats(d);
    });
    socket.on("beer:audience_play_update", (d) => { setAudiencePlayStats(d); });
    return () => {
      unbindResync();
      socket.off("connect", onConnect);
      socket.emit("beer:leave-global");
      socket.disconnect();
      if (audiencePlayTimerRef.current) clearInterval(audiencePlayTimerRef.current);
      if (prizeLadderTimerRef.current) clearTimeout(prizeLadderTimerRef.current);
      if (gameOverTimerRef.current) clearTimeout(gameOverTimerRef.current);
    };
  }, [loadState, deviceId, displayName, spectatorMode, stopBgMusic]);

  useEffect(() => {
    if (fffCountdown <= 0) return;
    const id = setTimeout(() => { setFffCountdown(prev => prev - 1); }, 1000);
    return () => clearTimeout(id);
  }, [fffCountdown]);

  useEffect(() => {
    if (fffTimer <= 0) return;
    if (fffDeadlineMsRef.current) {
      const tick = () => {
        const rem = Math.max(0, Math.ceil((fffDeadlineMsRef.current - Date.now()) / 1000));
        setFffTimer(rem);
        if (rem <= 0 && timerRef.current) clearInterval(timerRef.current);
      };
      tick();
      timerRef.current = setInterval(tick, 250);
      return () => clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => { setFffTimer(p => { if (p <= 1) { clearInterval(timerRef.current); return 0; } return p - 1; }); }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fffQuestion, fffTimerStarted, fffTimer > 0]);

  const submitFFFAnswer = async (answer) => {
    if (fffSubmitted || fffTimer <= 0) return;
    setFffAnswer(answer); setFffSubmitted(true);
    try { const r = await fetch(`${API_BASE}/api/beerillioner/sessions/${session.id}/fff/answer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answer, device_id: deviceId, display_name: displayName, round_number: fffRound, correct_answer: fffCorrectAnswer }) }); const d = await r.json(); setFffResult(d.is_correct ? "correct" : "wrong"); if (d.time_taken_seconds != null) setFffTimeTaken(d.time_taken_seconds); } catch { setFffResult("error"); }
  };

  const submitAudiencePlayAnswer = async (answer) => {
    if (audiencePlayLocked || audiencePlayPaused || audiencePlayTimer <= 0) return;
    setAudiencePlayAnswer(answer);
    try { await fetch(`${API_BASE}/api/beerillioner/sessions/${session.id}/audience-play/answer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answer, device_id: deviceId, display_name: displayName }) }); } catch { /* ignore */ }
  };

  const prizes = config?.prizes || [];
  const status = session?.status;

  const PrizeLadder = ({ fullHeight, showcase, outcome }) => {
    const liveCurrent = !outcome ? session?.current_level : null;
    const failedLevel = outcome?.failed_level || (outcome ? session?.current_level : null);
    const winLevel = outcome?.winning_level || 0;
    const reason = outcome?.reason;
    const isWinner = reason === "won";
    const isWrong = reason === "wrong" || reason === "timer";
    const isWalkaway = reason === "walkaway";

    return (
      <Box sx={{
        flex: fullHeight ? 1 : undefined,
        bgcolor: "rgba(13,33,55,0.9)",
        borderRadius: 1,
        border: "1px solid #1a3a5c",
        overflow: "auto",
        py: showcase ? 1 : 0.5,
        minHeight: fullHeight ? 0 : undefined,
        width: showcase ? "100%" : undefined,
        maxWidth: showcase ? 420 : undefined,
      }}>
        {[...prizes].reverse().map((p) => {
          let bgcolor = "transparent";
          let borderLeft = "3px solid transparent";
          let fontWeight = 400;
          let opacity = 1;
          let badge = null;
          const isLiveCurrent = liveCurrent === p.level;

          if (outcome) {
            if (isWinner) {
              if (p.level === winLevel) {
                bgcolor = "rgba(255,215,0,0.28)";
                borderLeft = "3px solid #ffd700";
                fontWeight = 800;
                badge = "WON";
              } else if (p.level < winLevel) {
                bgcolor = "rgba(0,230,118,0.1)";
              }
            } else if (isWrong && p.level === failedLevel) {
              bgcolor = "rgba(183,28,28,0.5)";
              borderLeft = "3px solid #ef5350";
              fontWeight = 800;
              badge = "INCORRECT";
            } else if (isWalkaway && p.level === failedLevel) {
              bgcolor = "rgba(255,152,0,0.22)";
              borderLeft = "3px solid #ff9800";
              fontWeight = 700;
              badge = "WALK AWAY";
            } else if (p.level <= winLevel) {
              bgcolor = "rgba(255,215,0,0.12)";
              if (p.is_checkpoint) badge = "SAFE";
            } else {
              opacity = 0.38;
            }
          } else if (isLiveCurrent) {
            bgcolor = "#ff9800";
            borderLeft = "3px solid #ffd700";
            fontWeight = 800;
          }

          return (
            <Box key={p.id} sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: showcase ? 2 : 1.5,
              py: showcase ? 1.1 : 0.6,
              bgcolor,
              borderLeft,
              opacity,
            }}>
              {p.is_checkpoint && <Typography sx={{ color: "#fff", fontSize: showcase ? "0.85rem" : "0.65rem" }}>◆</Typography>}
              <Typography sx={{ color: "#fff", fontSize: showcase ? "1rem" : "0.75rem", fontWeight: 600, minWidth: showcase ? 24 : 18 }}>{p.level}</Typography>
              {p.image_url ? <Box component="img" src={p.image_url.startsWith("http") ? p.image_url : `${API_BASE}/${p.image_url}`} sx={{ width: showcase ? 28 : 20, height: showcase ? 28 : 20, borderRadius: "50%", objectFit: "cover" }} /> : null}
              <Typography sx={{ color: "#fff", fontSize: showcase ? "0.95rem" : "0.7rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight }}>{p.label || p.value_text || `Level ${p.level}`}</Typography>
              {badge && (
                <Typography sx={{
                  color: badge === "INCORRECT" ? "#ffcdd2" : badge === "WALK AWAY" ? "#ffe0b2" : "#ffd700",
                  fontSize: showcase ? "0.65rem" : "0.55rem",
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  flexShrink: 0,
                }}>{badge}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };
  const isHotSeat = useMemo(() => {
    if (!session) return false;
    const name = session.hot_seat_display_name || hotSeatPlayer;
    if (!name) return false;
    if (session.hot_seat_player_id && deviceId === session.hot_seat_player_id) return true;
    if (!session.hot_seat_player_id) {
      const dn = displayName.trim().toLowerCase();
      const hs = String(name).trim().toLowerCase();
      if (dn && hs && dn === hs) return true;
    }
    return false;
  }, [session, hotSeatPlayer, deviceId, displayName]);

  const closeHotSeatAnnounce = useCallback(() => {
    setHotSeatAnnounce(null);
    setFffLeaderboardVisible(false);
    setFffPublicLeaderboard([]);
    setFffQuestion(null);
  }, []);

  const hotSeatModal = (
    <HotSeatAnnounceModal
      open={!!hotSeatAnnounce}
      onClose={closeHotSeatAnnounce}
      displayName={hotSeatAnnounce?.display_name}
      profilePicture={hotSeatAnnounce?.profile_picture}
      isSelf={
        !!hotSeatAnnounce?.player_id &&
        String(hotSeatAnnounce.player_id) === String(deviceId)
      }
      celebrate
    />
  );

  const fffRevealModal = (() => {
    const correct = fffReveal?.correct_answer;
    if (!correct) return null;
    const optionText =
      fffReveal?.options?.[`option_${String(correct).toLowerCase()}`] ??
      fffQuestion?.[`option_${String(correct).toLowerCase()}`] ??
      "";
    const gotItRight = !spectatorMode && fffSubmitted && fffAnswer === correct;
    const gotItWrong = !spectatorMode && fffSubmitted && fffAnswer !== correct;
    return (
      <Dialog
        open
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#0d2137",
            border: "2px solid #00e676",
            borderRadius: "24px",
            boxShadow: "0 0 40px rgba(0,230,118,0.35)",
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography sx={{ color: "#4fc3f7", letterSpacing: 2, fontSize: "0.8rem", fontWeight: 700, mb: 1.5 }}>
            CORRECT ANSWER
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
            <Typography variant="h3" sx={{ color: "#ffd700", fontWeight: 800 }}>
              {correct}
            </Typography>
            {optionText && (
              <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textAlign: "left" }}>
                {optionText}
              </Typography>
            )}
          </Box>
          {gotItRight && (
            <Typography sx={{ color: "#00e676", fontWeight: 700, mt: 1 }}>
              <CheckIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />
              You got it right!
            </Typography>
          )}
          {gotItWrong && (
            <Typography sx={{ color: "#ef5350", fontWeight: 700, mt: 1 }}>
              You answered {fffAnswer}
            </Typography>
          )}
          <Typography sx={{ color: "#888", fontSize: "0.8rem", mt: 2 }}>
            Leaderboard coming up...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  })();

  if (loading) {
    return (
      <>
        <SoundMuteButton floating />
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#050a14" }}>
          <CircularProgress sx={{ color: "#29b6f6" }} />
        </Box>
      </>
    );
  }

  if (!session || status === "ended") {
    if (!gameOver) {
      return (
        <>
          <SoundMuteButton floating />
          <BeerillionerWaitingView
            config={config}
            displayName={displayName}
            spectatorMode={spectatorMode}
            apiBase={API_BASE}
          />
        </>
      );
    }
    return (
      <>
        <SoundMuteButton floating />
        <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#050a14", p: { xs: 2, sm: 3 }, overflow: "auto" }}>
        {(() => {
          const isWinner = gameOver.reason === "won";
          const winLevel = gameOver.winning_level || 0;
          const winnerName = gameOver.winner_name || hotSeatPlayer || "Player";
          const lockedLetter = gameOver.highlighted_answer;
          const correctLetter = gameOver.correct_answer;
          const optText = (letter) => {
            if (!letter) return "";
            const key = `option_${String(letter).toLowerCase()}`;
            return gameOver[key] || gameOptions?.[key] || "";
          };
          const reasonCopy =
            gameOver.reason === "walkaway" ? "Player walked away" :
            gameOver.reason === "timer" ? "Time's up!" :
            gameOver.reason === "wrong" ? "Wrong answer!" :
            gameOver.reason === "admin_ended" ? "Host ended the game" : "";
          const showAnswerCompare = (gameOver.reason === "wrong" || gameOver.reason === "timer") && (lockedLetter || correctLetter);

          return (
            <Box sx={{ width: "100%", maxWidth: 920, mx: "auto", display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 3, md: 4 }, alignItems: { xs: "stretch", md: "flex-start" } }}>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {isWinner ? (
                  <>
                    <Typography sx={{ fontSize: { xs: "3rem", sm: "4rem" }, lineHeight: 1, mb: 0.5 }}>🏆</Typography>
                    <Typography variant="h3" sx={{ color: "#ffd700", fontWeight: 800, textShadow: "0 0 30px rgba(255,215,0,0.5)", mb: 1, fontSize: { xs: "1.8rem", sm: "2.4rem" } }}>WINNER!</Typography>
                    <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5, fontSize: { xs: "1.4rem", sm: "1.8rem" } }}>{winnerName}</Typography>
                    <Typography sx={{ color: "#4fc3f7", fontSize: "1.1rem", mb: 2 }}>Conquered all {winLevel} levels!</Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h3" sx={{ color: "#ffd700", fontWeight: 700, mb: 1, fontSize: { xs: "1.8rem", sm: "2.4rem" } }}>Game Over!</Typography>
                    {reasonCopy && <Typography variant="h5" sx={{ color: gameOver.reason === "walkaway" ? "#fff" : "#ef5350", mb: 1.5 }}>{reasonCopy}</Typography>}
                    {hotSeatPlayer && <Typography sx={{ color: "#4fc3f7", mb: 2 }}>{hotSeatPlayer}</Typography>}
                  </>
                )}

                {(gameOver.question_text || gameQuestion?.question_text) && (
                  <Box sx={{ bgcolor: "#0d2137", border: "1px solid #1a3a5c", borderRadius: 2, px: 2, py: 1.5, mb: 2, textAlign: "left" }}>
                    <Typography sx={{ color: "#4fc3f7", fontSize: "0.75rem", mb: 0.5 }}>Question</Typography>
                    <Typography sx={{ color: "#fff", fontSize: "0.95rem" }}>{gameOver.question_text || gameQuestion?.question_text}</Typography>
                  </Box>
                )}

                {showAnswerCompare && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 2 }}>
                    {lockedLetter && lockedLetter !== correctLetter && (
                      <Box sx={{ bgcolor: "rgba(183,28,28,0.35)", border: "2px solid #ef5350", borderRadius: 2, px: 2, py: 1.5, textAlign: "left" }}>
                        <Typography sx={{ color: "#ffcdd2", fontSize: "0.75rem", fontWeight: 700, mb: 0.3 }}>Locked answer (incorrect)</Typography>
                        <Typography sx={{ color: "#fff", fontWeight: 700 }}>{lockedLetter}{optText(lockedLetter) ? ` — ${optText(lockedLetter)}` : ""}</Typography>
                      </Box>
                    )}
                    {correctLetter && (
                      <Box sx={{ bgcolor: "rgba(27,94,32,0.4)", border: "2px solid #00e676", borderRadius: 2, px: 2, py: 1.5, textAlign: "left" }}>
                        <Typography sx={{ color: "#a5d6a7", fontSize: "0.75rem", fontWeight: 700, mb: 0.3 }}>Correct answer</Typography>
                        <Typography sx={{ color: "#fff", fontWeight: 700 }}>{correctLetter}{optText(correctLetter) ? ` — ${optText(correctLetter)}` : ""}</Typography>
                      </Box>
                    )}
                    {gameOver.reason === "timer" && !lockedLetter && (
                      <Typography sx={{ color: "#ef9a9a", fontSize: "0.9rem" }}>No answer was locked in time.</Typography>
                    )}
                  </Box>
                )}

                {isWinner && correctLetter && (
                  <Box sx={{ bgcolor: "rgba(27,94,32,0.35)", border: "2px solid #00e676", borderRadius: 2, px: 2, py: 1.5, mb: 2, textAlign: "left" }}>
                    <Typography sx={{ color: "#a5d6a7", fontSize: "0.75rem", fontWeight: 700, mb: 0.3 }}>Final answer</Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 700 }}>{correctLetter}{optText(correctLetter) ? ` — ${optText(correctLetter)}` : ""}</Typography>
                  </Box>
                )}

                {winLevel <= 0 && !isWinner && gameOver.reason !== "admin_ended" && (
                  <Typography sx={{ color: "#4fc3f7", mt: 1 }}>No prizes won. Better luck next time!</Typography>
                )}
              </Box>

              <Box sx={{ flex: { xs: "none", md: "0 0 340px" }, width: "100%" }}>
                <Typography sx={{ color: "#ffd700", fontWeight: 800, mb: 1, textAlign: "center", fontSize: "1.15rem" }}>Prize Ladder</Typography>
                <PrizeLadder showcase outcome={gameOver} />
              </Box>
            </Box>
          );
        })()}
      </Box>
      </>
    );
  }

  if (status === "idle") {
    return (
      <>
        <SoundMuteButton floating />
        <BeerillionerGetReadyView
          config={config}
          displayName={displayName}
          spectatorMode={spectatorMode}
          apiBase={API_BASE}
        />
      </>
    );
  }

  if (status === "fff_intro") {
    return (
      <>
        <SoundMuteButton floating />
        {hotSeatModal}
        <FFFIntroView />
      </>
    );
  }

  if ((status === "fff" && fffLeaderboardVisible) || (hotSeatAnnounce && fffLeaderboardVisible)) {
    return (
      <>
        <SoundMuteButton floating />
        {hotSeatModal}
        <FFFLeaderboardView
          entries={fffPublicLeaderboard}
          deviceId={deviceId}
          spectatorMode={spectatorMode}
        />
      </>
    );
  }

  if (status === "fff" && fffQuestion) {
    return (
    <>
    <SoundMuteButton floating />
    {hotSeatModal}
    {fffRevealModal}
    <Box sx={{ minHeight: "100vh", bgcolor: "#050a14", display: "flex", flexDirection: "column", p: 3 }}>
      {/* {(prizes.length > 0 || enabledLifelines.length > 0) && (
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {prizes.length > 0 && (
            <Paper sx={{ p: 1, bgcolor: "#0d1b2a", borderRadius: 1, border: "1px solid #333", display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: "#ffd700", fontWeight: 700, mr: 0.5 }}>LADDER:</Typography>
              {prizes.map(p => <Chip key={p.id} size="small" label={p.image_url ? undefined : (p.label || p.level)} icon={p.image_url ? <Box component="img" src={p.image_url.startsWith("http") ? p.image_url : `${API_BASE}/${p.image_url}`} sx={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} /> : undefined} sx={{ bgcolor: p.is_checkpoint ? "rgba(184,134,11,0.4)" : "rgba(255,255,255,0.08)", color: p.is_checkpoint ? "#ffd700" : "#888", fontSize: "0.65rem", height: 24 }} />)}
            </Paper>
          )}
          {enabledLifelines.length > 0 && (
            <Paper sx={{ p: 1, bgcolor: "#0d1b2a", borderRadius: 1, border: "1px solid #333", display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ color: "#ffd700", fontWeight: 700, mr: 0.5 }}>LIFELINES:</Typography>
              {enabledLifelines.map(ll => { const IconComp = ll.type === "50_50" ? SplitIcon : ll.type === "ask_audience" ? GroupsIcon : PhoneIcon; return <Chip key={ll.id} size="small" icon={<IconComp sx={{ fontSize: 14, color: "#ffd700 !important" }} />} label={ll.label} sx={{ bgcolor: "rgba(255,215,0,0.15)", color: "#ffd700", fontSize: "0.7rem", height: 24 }} />; })}
            </Paper>
          )}
        </Box>
      )} */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="h5" sx={{ color: "#29b6f6", fontWeight: 700, mb: 1 }}>Fastest Finger First!</Typography>
      {fffCountdown > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6 }}>
          <Typography variant="h1" sx={{ color: "#ffd700", fontWeight: 800, fontSize: { xs: "6rem", sm: "8rem" }, textShadow: "0 0 40px rgba(255,215,0,0.5)", animation: "pulse 1s ease-in-out infinite" }}>{fffCountdown}</Typography>
          <Typography sx={{ color: "#4fc3f7", fontSize: "1.2rem", mt: 2 }}>Get Ready...</Typography>
        </Box>
      ) : (<>
      {fffTimer > 0 && <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}><TimerIcon sx={{ color: fffTimer <= 5 ? "#ef5350" : "#4fc3f7" }} /><Typography variant="h4" sx={{ color: fffTimer <= 5 ? "#ef5350" : "#4fc3f7", fontWeight: 700 }}>{fffTimer}s</Typography></Box>}
      <Paper sx={{ p: 3, bgcolor: "#0d2137", border: "2px solid #29b6f6", borderRadius: "30px", maxWidth: 600, width: "100%", mb: 3, boxShadow: "0 0 15px rgba(41,182,246,0.2)" }}>
        <Typography variant="h6" sx={{ color: "#fff", textAlign: "center", mb: 2 }}>{fffQuestion.question_text}</Typography>
        <Grid container spacing={1.5}>{["A","B","C","D"].map(opt => {
          const isSelected = fffAnswer === opt;
          const isVisible = shownOptions.includes(opt);
          let bg = "#0d2137"; let borderColor = "#29b6f6";
          if (!isVisible) { bg = "#0a1520"; borderColor = "#1a3a5c"; }
          else if (fffSubmitted && fffResult === "correct" && isSelected) { bg = "#1b5e20"; borderColor = "#00e676"; }
          else if (fffSubmitted && fffResult === "wrong" && isSelected) { bg = "#b71c1c"; borderColor = "#ef5350"; }
          else if (isSelected) { bg = "rgba(255,152,0,0.2)"; borderColor = "#ff9800"; }
          return (<Grid item xs={12} key={opt}><Button fullWidth variant="contained" onClick={() => submitFFFAnswer(opt)} disabled={spectatorMode || !isVisible || fffSubmitted || fffTimer <= 0}
            sx={{ bgcolor: bg, border: `2px solid ${borderColor}`, color: isVisible ? "#fff" : "#444", py: 1.5, textTransform: "none", justifyContent: "flex-start", borderRadius: "25px", boxShadow: `0 0 8px ${borderColor}40`, "&:hover": { bgcolor: bg, filter: isVisible ? "brightness(1.2)" : "none" }, "&.Mui-disabled": { color: isVisible ? "#888" : "#333", bgcolor: bg } }}>
            <Typography component="span" sx={{ fontWeight: 800, mr: 1.5, color: isVisible ? "#ffd700" : "#333", fontSize: "1.1rem" }}>{opt}:</Typography><Typography sx={{ fontSize: "1rem" }}>{isVisible ? fffQuestion[`option_${opt.toLowerCase()}`] : "?"}</Typography>
          </Button></Grid>);
        })}</Grid>
      </Paper>
      {fffSubmitted && <Box sx={{ textAlign: "center" }}>
        {fffResult === "correct" && <Typography sx={{ color: "#4caf50", fontSize: "1.2rem" }}><CheckIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />Correct!</Typography>}
        {fffResult === "wrong" && <Typography sx={{ color: "#ef5350", fontSize: "1.2rem" }}>Wrong answer.</Typography>}
        {!fffResult && <Typography sx={{ color: "#aaa" }}>Submitting...</Typography>}
        {fffTimeTaken != null && <Typography sx={{ color: "#29b6f6", fontSize: "1rem", mt: 0.5 }}>Time: {fffTimeTaken}s</Typography>}
      </Box>}
      {fffTimer <= 0 && !fffSubmitted && fffCountdown <= 0 && fffTimerStarted && <Typography sx={{ color: "#ef5350", fontSize: "1.2rem" }}>Time&apos;s up!</Typography>}
      {fffTimer <= 0 && !fffSubmitted && fffCountdown <= 0 && !fffTimerStarted && <Typography sx={{ color: "#4fc3f7", fontSize: "1rem" }}>Waiting for host to start the timer...</Typography>}
      </>)}
      </Box>
    </Box>
    </>
  );
  }
  if (status === "fff" && !fffQuestion) {
    return (
      <>
        <SoundMuteButton floating />
        {hotSeatModal}
        {fffRevealModal}
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#050a14", p: 3 }}>
          <Typography sx={{ color: "#4fc3f7", fontSize: "1.1rem", textAlign: "center" }}>
            FFF is live. Waiting for question sync...
          </Typography>
        </Box>
      </>
    );
  }
  if (status === "main_game") {
    const enabledLifelines = (config?.lifelines || []).filter(l => l.is_enabled);
    const kbcBg = "#050a14";
    const kbcDarkBlue = "#0d2137";
    const kbcLightBlue = "#29b6f6";
    const kbcGlow = "#4fc3f7";
    const kbcOrange = "#ff9800";
    const kbcGold = "#ffd700";
    const kbcGreen = "#00e676";

    const LogoComp = () => (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
        <Box component="img" src="/MindYourMusic.png" alt="Logo" sx={{ maxWidth: { xs: 140, sm: 180, md: 220 }, maxHeight: { xs: 140, sm: 180, md: 220 }, objectFit: "contain" }} />
      </Box>
    );

    const LifelinesRow = ({ compact }) => (
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: compact ? "center" : "flex-end", flexWrap: "wrap" }}>
        {enabledLifelines.map(ll => {
          const available = lifelinesRemaining.includes(ll.type);
          const IconComp = ll.type === "50_50" ? SplitIcon : ll.type === "ask_audience" ? GroupsIcon : PhoneIcon;
          return (
            <Box key={ll.id} sx={{ bgcolor: kbcDarkBlue, border: `1px solid ${available ? kbcLightBlue : "#1a3a5c"}`, borderRadius: "50px", px: 1.5, py: 0.5, display: "flex", alignItems: "center", gap: 0.5, opacity: available ? 1 : 0.4 }}>
              <IconComp sx={{ fontSize: 18, color: available ? kbcGlow : "#555" }} />
              {ll.type === "50_50" && <Typography sx={{ color: available ? "#fff" : "#555", fontSize: "0.7rem", fontWeight: 700 }}>50:50</Typography>}
            </Box>
          );
        })}
      </Box>
    );

    const audiencePlayEnabled = !spectatorMode && config?.audience_play_enabled;
    const audiencePlayVisible = audiencePlayEnabled && (audiencePlayActive || audiencePlayPaused || audiencePlayLocked);
    const canAudienceAnswer = !spectatorMode && audiencePlayActive && !audiencePlayLocked && !audiencePlayPaused && audiencePlayTimer > 0;
    const showAudienceLifelinePoll = audienceResults && audienceGraphVisible;

    const AudiencePlayBar = () => (!isHotSeat && audiencePlayVisible) ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, px: 2, py: 1, bgcolor: "rgba(224,64,251,0.08)", border: "1px solid rgba(224,64,251,0.25)", borderRadius: "20px" }}>
        <GroupsIcon sx={{ color: "#e040fb", fontSize: 20 }} />
        <Typography sx={{ color: "#e040fb", fontWeight: 700, fontSize: "0.85rem" }}>YOUR TURN</Typography>
        {audiencePlayActive && !audiencePlayLocked && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
            <TimerIcon sx={{ color: audiencePlayTimer <= 3 ? "#ef5350" : "#e040fb", fontSize: 18 }} />
            <Typography sx={{ color: audiencePlayTimer <= 3 ? "#ef5350" : "#e040fb", fontWeight: 700, fontSize: "1.1rem" }}>{audiencePlayTimer}s</Typography>
          </Box>
        )}
        {audiencePlayPaused && <Typography sx={{ color: kbcOrange, fontWeight: 700, ml: "auto", fontSize: "0.8rem" }}>PAUSED — Lifeline</Typography>}
        {audiencePlayLocked && <Typography sx={{ color: "#ef5350", fontWeight: 700, ml: "auto", fontSize: "0.8rem" }}>LOCKED</Typography>}
      </Box>
    ) : null;

    const AudienceLifelinePendingBanner = () => (isHotSeat && audienceLifelinePending) ? (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: "rgba(255,215,0,0.08)", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 2, textAlign: "center" }}>
        <Typography sx={{ color: kbcGold, fontWeight: 700, fontSize: "0.9rem" }}>Ask the Audience — waiting for audience timer...</Typography>
      </Box>
    ) : null;

    const renderOption = (opt) => {
      const isRemoved = removedOptions.includes(opt);
      const isVisible = shownOptions.includes(opt);
      const isHighlighted = highlightedAnswer === opt;
      const isCorrectRevealed = revealResult?.correct_answer === opt;
      const isWrongRevealed = revealResult && !revealResult.is_correct && revealResult.highlighted_answer === opt;
      const isIndivCorrect = revealedOptions[opt] === true;
      const isIndivWrong = revealedOptions[opt] === false;
      const isMyAnswer = audiencePlayAnswer === opt;
      const clickable = !spectatorMode && !isHotSeat && canAudienceAnswer && isVisible && !isRemoved;
      let borderColor = isVisible ? kbcLightBlue : "#1a3a5c";
      let bgColor = isVisible ? kbcDarkBlue : "#0a1520";
      if (isRemoved) { bgColor = "#0a1520"; borderColor = "#1a3a5c"; }
      else if (isWrongRevealed) { bgColor = "#b71c1c"; borderColor = "#ef5350"; }
      else if ((isCorrectRevealed && revealResult) || isIndivCorrect) { bgColor = "#1b5e20"; borderColor = kbcGreen; }
      else if (isIndivWrong) { bgColor = "#1a1a2e"; borderColor = "#555"; }
      else if (isHighlighted) { bgColor = "rgba(255,152,0,0.2)"; borderColor = kbcOrange; }
      else if (isMyAnswer && !revealResult) { borderColor = "#e040fb"; }
      return (
        <Grid item xs={12} sm={6} key={opt}>
          <Box onClick={clickable ? () => submitAudiencePlayAnswer(opt) : undefined}
            sx={{ bgcolor: bgColor, border: `2px solid ${borderColor}`, borderRadius: "25px", px: { xs: 1.5, sm: 2 }, py: { xs: 1.2, sm: 1.5 }, display: "flex", alignItems: "center", gap: 1,
              boxShadow: isMyAnswer && !revealResult ? "0 0 12px rgba(224,64,251,0.35)" : isHighlighted ? "0 0 12px rgba(255,152,0,0.3)" : "0 0 8px rgba(41,182,246,0.15)",
              opacity: isRemoved || isIndivWrong ? 0.35 : !isVisible ? 0.3 : 1, transition: "all 0.3s ease",
              cursor: clickable ? "pointer" : "default", "&:hover": clickable ? { filter: "brightness(1.2)" } : {} }}>
            {isHighlighted && !revealResult && <Typography sx={{ color: kbcGold, fontSize: "0.8rem" }}>◆</Typography>}
            {isMyAnswer && !revealResult && !isHighlighted && <Typography sx={{ color: "#e040fb", fontSize: "0.8rem" }}>★</Typography>}
            <Typography sx={{ fontWeight: 800, color: !isVisible || isRemoved || isIndivWrong ? "#444" : kbcGold, fontSize: { xs: "0.9rem", sm: "1rem" }, minWidth: 22 }}>{opt}:</Typography>
            <Typography sx={{ color: !isVisible || isRemoved || isIndivWrong ? "#444" : "#fff", fontSize: { xs: "0.9rem", sm: "1rem" }, flex: 1 }}>{!isVisible ? "?" : isRemoved ? "—" : (gameOptions?.[`option_${opt.toLowerCase()}`] || "...")}</Typography>
            {isIndivCorrect && <Typography sx={{ color: kbcGreen, ml: "auto" }}>✓</Typography>}
            {isIndivWrong && <Typography sx={{ color: "#ef5350", ml: "auto" }}>✗</Typography>}
            {isMyAnswer && !revealResult && !isIndivCorrect && !isIndivWrong && <Typography sx={{ color: "#e040fb", ml: "auto", fontSize: "0.7rem", fontWeight: 700 }}>YOUR</Typography>}
          </Box>
        </Grid>
      );
    };

    const HotSeatBanner = () => {
      if (!hotSeatPlayer) return null;
      return (
        <Box sx={{ flexShrink: 0, px: 2, pt: 1.5, pb: 0.5, zIndex: 1 }}>
          <Typography sx={{ color: kbcGold, fontWeight: 800, fontSize: { xs: "1.05rem", sm: "1.25rem" }, textAlign: "center" }}>
            Hot Seat: {hotSeatPlayer}{!spectatorMode && isHotSeat ? " (You)" : ""}
          </Typography>
        </Box>
      );
    };

    return (
      <Box sx={{ height: "100vh", minHeight: "100vh", bgcolor: kbcBg, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
        <SoundMuteButton floating sx={{ top: 8, right: 8 }} />
        {hotSeatModal}
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "80%", height: "80%", background: "radial-gradient(ellipse at center, rgba(41,182,246,0.08) 0%, transparent 60%)", pointerEvents: "none" }} />
        <HotSeatBanner />

        {showPrizeLadder && (
          <Box sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            bgcolor: "rgba(5,10,20,0.96)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 3,
          }}>
            <Typography sx={{ color: kbcGold, fontWeight: 800, fontSize: { xs: "1.4rem", sm: "1.8rem" }, mb: 0.5, textAlign: "center" }}>
              Prize Ladder
            </Typography>
            <Typography sx={{ color: kbcGlow, mb: 2, textAlign: "center" }}>
              {`Level ${session.current_level} secured`}
            </Typography>
            <PrizeLadder showcase />
          </Box>
        )}

        {(isMobile || isTablet) ? (
          /* Mobile & Tablet: Question first for visibility, Logo below */
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto" }}>
            <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 4 }, pt: { xs: 1.5, sm: 2 }, pb: { xs: 3, sm: 4 } }}>
              {gameQuestion ? (
                <Box sx={{ maxWidth: 640, mx: "auto", width: "100%" }}>
                  <Box sx={{ bgcolor: kbcDarkBlue, border: `2px solid ${kbcLightBlue}`, borderRadius: "30px", px: { xs: 2.5, sm: 3.5 }, py: { xs: 1.5, sm: 2 }, mb: 1.5, boxShadow: `0 0 15px rgba(41,182,246,0.2)` }}>
                    <Typography sx={{ color: "#fff", fontSize: { xs: "1rem", sm: "1.15rem" }, fontWeight: 600, textAlign: "center" }}>{gameQuestion.question_text}</Typography>
                  </Box>
                  {(clueVisible && questionClue) || (songNameVisible && songName) ? (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 1.5 }}>
                      {clueVisible && questionClue && (
                        <Box sx={{ bgcolor: "rgba(255,152,0,0.12)", border: "1px solid rgba(255,152,0,0.4)", borderRadius: "20px", px: 2, py: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ color: kbcOrange, fontSize: "0.8rem", fontWeight: 700 }}>Clue:</Typography>
                          <Typography sx={{ color: "#fff", fontSize: "0.8rem" }}>{questionClue}</Typography>
                        </Box>
                      )}
                      {songNameVisible && songName && (
                        <Box sx={{ bgcolor: "rgba(41,182,246,0.12)", border: "1px solid rgba(41,182,246,0.4)", borderRadius: "20px", px: 2, py: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ color: kbcLightBlue, fontSize: "0.8rem", fontWeight: 700 }}>Song:</Typography>
                          <Typography sx={{ color: "#fff", fontSize: "0.8rem" }}>{songName}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : null}
                  <AudiencePlayBar />
                  {questionTimerValue != null && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1.5 }}>
                      <TimerIcon sx={{ color: questionTimerValue <= 5 ? "#ef5350" : kbcGlow }} />
                      <Typography variant="h4" sx={{ color: questionTimerValue <= 5 ? "#ef5350" : kbcGlow, fontWeight: 700, fontSize: { xs: "1.8rem", sm: "2rem" } }}>{questionTimerValue}s</Typography>
                    </Box>
                  )}
                  <Grid container spacing={1.5}>
                    {["A","B","C","D"].map(renderOption)}
                  </Grid>
                  <AudienceLifelinePendingBanner />
                </Box>
              ) : (
                <Box sx={{ maxWidth: 640, mx: "auto", width: "100%", bgcolor: kbcDarkBlue, border: `2px solid ${kbcLightBlue}`, borderRadius: "30px", px: 4, py: 3, textAlign: "center" }}>
                  <Typography sx={{ color: kbcGlow }}>Waiting for the next question...</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2, minHeight: { xs: 100, sm: 140 } }}>
              <LogoComp />
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}><LifelinesRow compact /></Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <IconButton onClick={() => setLadderModalOpen(true)} sx={{ color: kbcLightBlue, bgcolor: kbcDarkBlue, "&:hover": { bgcolor: "#1a3a5c" } }} title="View Prize Ladder & Lifelines"><VisibilityIcon /></IconButton>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Desktop: CSS Grid - Logo in middle, Questions at bottom, Right sidebar */
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 300px", gridTemplateRows: "1fr auto", minHeight: 0, overflow: "hidden" }}>
            {/* Left column: Logo centered in middle, Questions at bottom */}
            <Box sx={{ gridColumn: 1, gridRow: "1 / span 2", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
                <LogoComp />
              </Box>
              <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 4, overflow: "auto" }}>
              {gameQuestion ? (
                <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
                  <Box sx={{ bgcolor: kbcDarkBlue, border: `2px solid ${kbcLightBlue}`, borderRadius: "30px", px: 4, py: 2.5, mb: 2, boxShadow: `0 0 15px rgba(41,182,246,0.2)` }}>
                    <Typography sx={{ color: "#fff", fontSize: "1.2rem", fontWeight: 600, textAlign: "center" }}>{gameQuestion.question_text}</Typography>
                  </Box>
                  {(clueVisible && questionClue) || (songNameVisible && songName) ? (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mb: 2 }}>
                      {clueVisible && questionClue && (
                        <Box sx={{ bgcolor: "rgba(255,152,0,0.12)", border: "1px solid rgba(255,152,0,0.4)", borderRadius: "20px", px: 2.5, py: 0.7, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ color: kbcOrange, fontSize: "0.85rem", fontWeight: 700 }}>Clue:</Typography>
                          <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>{questionClue}</Typography>
                        </Box>
                      )}
                      {songNameVisible && songName && (
                        <Box sx={{ bgcolor: "rgba(41,182,246,0.12)", border: "1px solid rgba(41,182,246,0.4)", borderRadius: "20px", px: 2.5, py: 0.7, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography sx={{ color: kbcLightBlue, fontSize: "0.85rem", fontWeight: 700 }}>Song:</Typography>
                          <Typography sx={{ color: "#fff", fontSize: "0.85rem" }}>{songName}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : null}
                  <AudiencePlayBar />
                  {questionTimerValue != null && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                      <TimerIcon sx={{ color: questionTimerValue <= 5 ? "#ef5350" : kbcGlow }} />
                      <Typography variant="h4" sx={{ color: questionTimerValue <= 5 ? "#ef5350" : kbcGlow, fontWeight: 700 }}>{questionTimerValue}s</Typography>
                    </Box>
                  )}
                  <Grid container spacing={1.5}>
                    {["A","B","C","D"].map(renderOption)}
                  </Grid>
                  <AudienceLifelinePendingBanner />
                </Box>
              ) : (
                <Box sx={{ maxWidth: 720, mx: "auto", width: "100%", bgcolor: kbcDarkBlue, border: `2px solid ${kbcLightBlue}`, borderRadius: "30px", px: 4, py: 3, textAlign: "center" }}>
                  <Typography sx={{ color: kbcGlow }}>Waiting for the next question...</Typography>
                </Box>
              )}
              </Box>
            </Box>
            {/* Right column: Prize Ladder (top) + Lifelines (full height), spans both rows */}
            <Box sx={{ gridColumn: 2, gridRow: "1 / span 2", display: "flex", flexDirection: "column", p: 1.5, bgcolor: "rgba(13,33,55,0.6)", borderLeft: "1px solid #1a3a5c", overflow: "hidden", minHeight: 0 }}>
              <Box sx={{ flexShrink: 0, mb: 1 }}>
                <Typography variant="caption" sx={{ color: kbcLightBlue, fontWeight: 700, mb: 0.5, display: "block" }}>Prize Ladder</Typography>
                <PrizeLadder fullHeight={false} />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Typography variant="caption" sx={{ color: kbcLightBlue, fontWeight: 700, mb: 0.5, display: "block" }}>Lifelines</Typography>
                <LifelinesRow />
              </Box>
            </Box>
          </Box>
        )}

        <Dialog open={showAudienceLifelinePoll} PaperProps={{ sx: { bgcolor: kbcDarkBlue, border: `2px solid ${kbcGold}`, borderRadius: 3, minWidth: { xs: "85vw", sm: 360 }, maxWidth: 420 } }}>
          <DialogTitle sx={{ color: kbcGold, borderBottom: `1px solid rgba(255,215,0,0.3)`, display: "flex", alignItems: "center", gap: 1, pb: 1.5 }}>
            <GroupsIcon sx={{ color: kbcGold }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: kbcGold }}>Ask the Audience</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 3 }}>
            {audienceResults && (
              <Box>
                <Typography variant="subtitle2" sx={{ color: kbcGlow, textAlign: "center", mb: 2.5, fontSize: "0.95rem" }}>
                  {audienceResults.total_votes} vote{audienceResults.total_votes !== 1 ? "s" : ""}
                </Typography>
                {["A","B","C","D"].map(opt => !removedOptions.includes(opt) && (
                  <Box key={opt} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Typography sx={{ color: kbcGold, minWidth: 20, fontWeight: 800, fontSize: "1.1rem" }}>{opt}</Typography>
                    <Box sx={{ flex: 1, position: "relative", height: 28, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                      <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${audienceResults.percentages?.[opt] || 0}%`, background: `linear-gradient(90deg, ${kbcLightBlue}, ${kbcGlow})`, borderRadius: 2, transition: "width 0.6s ease" }} />
                    </Box>
                    <Typography sx={{ color: kbcGold, minWidth: 44, textAlign: "right", fontWeight: 700, fontSize: "1rem" }}>{audienceResults.percentages?.[opt] || 0}%</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={ladderModalOpen} onClose={() => setLadderModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { bgcolor: kbcDarkBlue, border: `1px solid ${kbcLightBlue}` } }}>
          <DialogTitle sx={{ color: kbcGold, borderBottom: `1px solid ${kbcLightBlue}` }}>Prize Ladder & Lifelines</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="subtitle2" sx={{ color: kbcLightBlue, mb: 1 }}>Lifelines</Typography>
            <LifelinesRow compact />
            <Typography variant="subtitle2" sx={{ color: kbcLightBlue, mt: 2, mb: 1 }}>Prize Ladder</Typography>
            <PrizeLadder fullHeight={false} />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return (
    <>
      <SoundMuteButton floating />
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#050a14" }}>
        <Typography sx={{ color: "#29b6f6" }}>Connecting to game...</Typography>
      </Box>
    </>
  );
};

export default BeerillionerScreen;
